import { useCallback, useEffect, useState, ReactNode } from 'react'
import useEmblaCarousel from 'embla-carousel-react'

import {
  EmblaViewport,
  EmblaContainer,
  EmblaSlide,
} from '../../../display/EmblaCarousel'
import ScrollingDots from '../../../display/EmblaCarousel/ScrollingDots'

type KeyedSlide = {
  key: string
  // A render function, not a component: invoking it (rather than mounting
  // <Component />) lets React reconcile each slide's stable element tree in
  // place when the slide list grows, instead of remounting visible slides.
  renderSlide: (props: { isActive: boolean }) => ReactNode
}

type EmblaSliderProps = {
  title: string
  portrait?: boolean
  keyedSlides: KeyedSlide[]
  arrows?: ReactNode
  onScrollPrev?: (callback: () => void) => void
  onScrollNext?: (callback: () => void) => void
}

const EmblaSlider = ({
  title,
  portrait,
  keyedSlides,
  arrows,
  onScrollPrev,
  onScrollNext,
}: EmblaSliderProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true })
  const [slideIndex, setSlideIndex] = useState(0)

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSlideIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return undefined

    onSelect()
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)

    if (onScrollPrev) onScrollPrev(() => emblaApi.scrollPrev())
    if (onScrollNext) onScrollNext(() => emblaApi.scrollNext())

    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('reInit', onSelect)
    }
  }, [emblaApi, onSelect, onScrollPrev, onScrollNext])

  const ariaLabel = `Images for ${title}`

  return (
    <div
      aria-label={ariaLabel}
      style={{ height: '100%', position: 'relative' }}
    >
      <EmblaViewport ref={emblaRef}>
        <EmblaContainer>
          {keyedSlides.map(({ key, renderSlide }, index) => (
            <EmblaSlide key={key}>
              {renderSlide({ isActive: slideIndex === index })}
            </EmblaSlide>
          ))}
        </EmblaContainer>
      </EmblaViewport>

      <ScrollingDots
        activeIndex={slideIndex}
        totalSlides={keyedSlides.length}
        onDotClick={(index) => emblaApi?.scrollTo(index)}
        portrait={portrait}
      />

      {arrows}
    </div>
  )
}

export default EmblaSlider
